import { useState } from 'react'
import type { ReactNode } from 'react'
import { Button } from '../../../shared/ui'
import { toast } from '../../../lib/toast'

/** Plain message from anything that was thrown. */
export function errorMessage(e: unknown): string {
  if (e instanceof Error && e.message) return e.message
  return 'Something went wrong.'
}

/** Toast an error and return its message for inline display. */
export function reportError(e: unknown): string {
  const msg = errorMessage(e)
  toast(msg, 'error')
  return msg
}

/** Whole number from a text input, or NaN when it is blank or not whole. */
export function parseWhole(s: string): number {
  return /^\d+$/.test(s.trim()) ? Number(s.trim()) : Number.NaN
}

export function ErrorLine({ children }: { children?: ReactNode }) {
  if (!children) return null
  return (
    <div className="os-error" role="alert">
      {children}
    </div>
  )
}

/** Labelled form control with an optional hint. `id` must match the control. */
export function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string
  label: string
  hint?: string
  error?: string | undefined
  children: ReactNode
}) {
  return (
    <div className="settings-row os-field">
      <label className="settings-label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint && <div className="settings-hint">{hint}</div>}
      <ErrorLine>{error}</ErrorLine>
    </div>
  )
}

/** Button that needs a second click. Cancel returns to the first state. */
export function ConfirmButton({
  label,
  confirmLabel,
  note,
  onConfirm,
  disabled,
  variant = 'danger',
}: {
  label: string
  confirmLabel: string
  note?: string
  onConfirm: () => void
  disabled?: boolean
  variant?: 'danger' | 'secondary'
}) {
  const [armed, setArmed] = useState(false)
  if (!armed) {
    return (
      <Button variant={variant} disabled={disabled} onClick={() => setArmed(true)}>
        {label}
      </Button>
    )
  }
  return (
    <span className="os-confirm" role="group" aria-label={`Confirm: ${label}`}>
      {note && <span className="settings-hint os-confirm-note">{note}</span>}
      <Button
        variant={variant}
        onClick={() => {
          setArmed(false)
          onConfirm()
        }}
      >
        {confirmLabel}
      </Button>
      <Button variant="secondary" onClick={() => setArmed(false)}>
        Cancel
      </Button>
    </span>
  )
}

/** Styles for the settings tabs. Everything uses tokens. */
export function OutreachStyles() {
  return (
    <style>{`
.os-root{display:flex;flex-direction:column;gap:var(--space-4)}
.os-tabs{display:flex;gap:var(--space-1);border-bottom:1px solid var(--border);flex-wrap:wrap}
.os-tab{background:none;border:none;border-bottom:2px solid transparent;color:var(--text-dim);font-family:var(--font-ui);font-size:var(--text-base);padding:var(--space-2) var(--space-4);cursor:pointer}
.os-tab:hover{color:var(--text)}
.os-tab[aria-selected="true"]{color:var(--accent);border-bottom-color:var(--accent)}
.os-bar{display:flex;gap:var(--space-2);align-items:center;flex-wrap:wrap}
.os-bar-end{margin-left:auto}
.os-list{display:flex;flex-direction:column;gap:var(--space-2)}
.os-card{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius);padding:var(--space-3) var(--space-4);display:flex;flex-direction:column;gap:var(--space-2)}
.os-card-head{display:flex;gap:var(--space-3);align-items:center;flex-wrap:wrap}
.os-name{font-size:var(--text-md);font-weight:600;color:var(--text)}
.os-meta{display:flex;gap:var(--space-4);flex-wrap:wrap;font-size:var(--text-sm);color:var(--text-dim)}
.os-actions{display:flex;gap:var(--space-2);align-items:center;flex-wrap:wrap}
.os-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:0 var(--space-4)}
.os-field{margin-bottom:var(--space-3)}
.os-fieldset{border:none;padding:0;margin:0 0 var(--space-3);min-width:0}
.os-error{color:var(--red);font-size:var(--text-sm)}
.os-warn{color:var(--amber);font-size:var(--text-sm)}
.os-ok{color:var(--green);font-size:var(--text-sm)}
.os-confirm{display:inline-flex;gap:var(--space-2);align-items:center;flex-wrap:wrap}
.os-confirm-note{margin:0}
.os-switch{display:inline-flex;align-items:center;gap:var(--space-2);background:none;border:none;cursor:pointer;color:var(--text);font-family:var(--font-ui);font-size:var(--text-sm);padding:0}
.os-switch-track{width:34px;height:18px;border-radius:999px;background:var(--bg3);border:1px solid var(--border2);position:relative;transition:background .15s}
.os-switch-track::after{content:"";position:absolute;top:1px;left:1px;width:14px;height:14px;border-radius:50%;background:var(--text-dim);transition:transform .15s}
.os-switch[aria-checked="true"] .os-switch-track{background:var(--green-glow);border-color:var(--green)}
.os-switch[aria-checked="true"] .os-switch-track::after{transform:translateX(16px);background:var(--green)}
.os-switch:disabled{opacity:.5;cursor:default}
.os-days{display:flex;gap:var(--space-3);flex-wrap:wrap}
.os-check{display:inline-flex;gap:var(--space-2);align-items:center;font-size:var(--text-base);color:var(--text)}
.os-ramp-row{display:flex;gap:var(--space-2);align-items:center;margin-bottom:var(--space-2);flex-wrap:wrap}
.os-ramp-row .settings-input{width:auto;flex:1;min-width:120px}
.os-table{width:100%;border-collapse:collapse;font-size:var(--text-sm)}
.os-table th{text-align:left;color:var(--text-dim);font-weight:500;padding:var(--space-2) var(--space-3);border-bottom:1px solid var(--border)}
.os-table td{padding:var(--space-2) var(--space-3);border-bottom:1px solid var(--border);color:var(--text);vertical-align:top;word-break:break-word}
.os-steps{display:flex;flex-direction:column;gap:var(--space-3)}
.os-step{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius);padding:var(--space-3) var(--space-4)}
.os-step-selected{border-color:var(--accent)}
.os-step textarea{min-height:110px;resize:vertical;font-family:var(--font-ui)}
.os-chips{display:flex;gap:var(--space-2);flex-wrap:wrap}
.os-chip{background:var(--bg3);border:1px solid var(--border);border-radius:999px;padding:2px var(--space-3);font-size:var(--text-sm);color:var(--text)}
.os-preview{background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);padding:var(--space-3) var(--space-4)}
.os-preview-body{white-space:pre-wrap;font-size:var(--text-base);color:var(--text)}
.os-preview-subject{font-weight:600;color:var(--text);margin-bottom:var(--space-2)}
`}</style>
  )
}
